/* /assets/product-recommendations.js */
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.placeholder = this.querySelector('.recommendations-container-placeholder');
    this.recommendationsContainer = this.querySelector('[data-recommendations-container]');
    this.slider = this.querySelector('[data-recommendations-slider]');
    this.titleElement = this.querySelector('[data-recommendations-title]');
    this.settings = JSON.parse(this.dataset.settings);
    this.cartProductIds = this.getCartProductIdsFromLocalStorage();
    this.isUpdating = false;
  }

  connectedCallback() {
    // Always listen to cart changes
    document.addEventListener('theme:cart:change', this.handleCartUpdate.bind(this));
    
    // Initialize visibility based on current cart state
    this.initializeProductVisibility();
    
    if (this.settings.recom_enable_shopify_recommendations && this.cartProductIds.length > 0) {
      this.fetchAndPrependDynamicRecommendations();
    }
  }

  initializeProductVisibility() {
    // Set initial visibility based on what's already in cart
    if (this.slider) {
      const allSlides = this.slider.querySelectorAll('[data-product-id]');
      allSlides.forEach(slide => {
        const productId = parseInt(slide.getAttribute('data-product-id'), 10);
        const isInCart = this.cartProductIds.includes(productId);
        this.setProductVisibility(slide, !isInCart);
      });
    }
  }

  async handleCartUpdate(event) {
    if (this.isUpdating) return;

    try {
      // Get current cart state
      const cart = event.detail?.cart || await fetch('/cart.js').then(res => res.json());
      
      // Get ALL product IDs currently in cart (no filtering by source)
      const currentCartProductIds = cart.items.map(item => item.product_id).sort();
      

      
      // Compare with stored cart state
      const cartChanged = JSON.stringify(currentCartProductIds) !== JSON.stringify(this.cartProductIds);
      
      if (cartChanged) {
        // Update stored cart state
        this.cartProductIds = currentCartProductIds;
        this.updateCartProductIdsInLocalStorage();
        
        // Update visibility for ALL products in recommendations
        this.updateAllProductVisibility();
        
        // Update title
        this.updateTitle();
        
        // Fetch dynamic recommendations if needed
        if (this.settings.recom_enable_shopify_recommendations && currentCartProductIds.length > 0) {
          await this.fetchAndPrependDynamicRecommendations();
        } else {
          // If no dynamic recommendations, just manage fallback and update navigation
          this.manageFallbackRecommendations();
          setTimeout(() => this.updateSliderNavigation(), 100);
        }
      }
      
    } catch (error) {
      console.error('Error handling cart update:', error);
    }
  }

  updateAllProductVisibility() {
    if (!this.slider) return;
    
    const allSlides = this.slider.querySelectorAll('[data-product-id]');
    
    allSlides.forEach(slide => {
      const productId = parseInt(slide.getAttribute('data-product-id'), 10);
      const isInCart = this.cartProductIds.includes(productId);
      this.setProductVisibility(slide, !isInCart);
    });
    
    // Don't update navigation here - wait for all visibility management to complete
  }

  setProductVisibility(slide, isVisible) {
    if (isVisible) {
      // Show the product
      slide.style.display = '';
      slide.style.opacity = '1';
      slide.classList.remove('hidden-from-cart');
    } else {
      // Hide the product
      slide.style.display = 'none';
      slide.style.opacity = '0';
      slide.classList.add('hidden-from-cart');
    }
  }

  updateSliderNavigation() {
    const productSlider = this.querySelector('product-slider');
    if (productSlider && typeof productSlider.updateNav === 'function') {
      productSlider.updateNav();
    }
  }

  async fetchAndPrependDynamicRecommendations() {
    if (!this.cartProductIds.length || this.isUpdating) return;
    
    this.isUpdating = true;
    
    try {
      // Clear existing dynamic recommendations
      this.clearDynamicRecommendations();
      
      // Limit to 3 random product IDs for API efficiency
      const productIdsToQuery = this.getRandomProductIds(this.cartProductIds, 3);
      
      // Fetch recommendations for each product
      const allRecommendations = await Promise.all(
        productIdsToQuery.map(productId => this.fetchRecommendationsForProduct(productId))
      );
      
      // Merge and process all recommendations
      let mergedRecommendations = this.mergeDynamicRecommendations(allRecommendations);
      
      // Filter out unavailable products and products with more than one variant
      mergedRecommendations = mergedRecommendations.filter(product => {
        const variantCount = Array.isArray(product.variants) ? product.variants.length : 0;
        return product.available && variantCount <= 1;
      });
      
      // Randomize and limit to max recommendations
      mergedRecommendations = this.shuffleArray(mergedRecommendations)
        .slice(0, this.settings.recom_max_recommendations);
      
      // Prepend to slider
      await this.prependDynamicRecommendations(mergedRecommendations);
      
      // Update visibility based on cart state
      this.updateAllProductVisibility();
      
      // Manage fallback visibility
      this.manageFallbackRecommendations();
      
      // Update slider navigation AFTER all visibility management is complete
      this.finalizeSliderState();
      
    } catch (error) {
      console.error('Error fetching dynamic recommendations:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  async fetchRecommendationsForProduct(productId) {
    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10`);
      if (!response.ok) return [];
      
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error(`Error fetching recommendations for product ${productId}:`, error);
      return [];
    }
  }

  mergeDynamicRecommendations(recommendationArrays) {
    const allRecommendations = recommendationArrays.flat();
    const uniqueRecommendations = [];
    const seenIds = new Set();
    
    allRecommendations.forEach(product => {
      if (!seenIds.has(product.id)) {
        seenIds.add(product.id);
        uniqueRecommendations.push(product);
      }
    });
    
    return uniqueRecommendations;
  }

  async prependDynamicRecommendations(recommendations) {
    if (!recommendations.length || !this.slider) return;
    
    const fragment = document.createDocumentFragment();
    
    for (const product of recommendations) {
      const slide = await this.createRecommendationSlide(product, 'dynamic');
      fragment.appendChild(slide);
    }
    
    // Prepend all dynamic recommendations at once
    this.slider.insertBefore(fragment, this.slider.firstChild);
    
    // Scroll to the beginning to show the first recommendation (but don't update nav yet)
    this.slider.scrollLeft = 0;
  }

  finalizeSliderState() {
    // Scroll to the beginning to show the first recommendation
    this.slider.scrollLeft = 0;
    
    // Update slider navigation to count ALL visible slides (dynamic + fallback)
    setTimeout(() => this.updateSliderNavigation(), 100);
  }

  async createRecommendationSlide(product, source) {
    const slide = document.createElement('div');
    slide.className = 'product-recommendations__slide';
    slide.setAttribute('data-dynamic-recommendation', '');
    slide.setAttribute('data-product-id', product.id);
    
    // Transform Shopify API product format to match our recommendation format
    const productData = this.transformProductData(product);
    
    const recommendedProduct = document.createElement('recommended-product');
    recommendedProduct.setAttribute('data-product', JSON.stringify(productData));
    recommendedProduct.setAttribute('data-source', source);
    
    slide.appendChild(recommendedProduct);
    return slide;
  }

  transformProductData(shopifyProduct) {
    // Transform Shopify API format to our internal format
    return {
      id: shopifyProduct.id,
      title: shopifyProduct.title,
      url: `/products/${shopifyProduct.handle}`,
      featured_image: shopifyProduct.featured_image,
      variants: shopifyProduct.variants.map(variant => ({
        id: variant.id,
        title: variant.title,
        available: variant.available,
        price: variant.price,
        featured_image: variant.featured_image?.src || shopifyProduct.featured_image
      }))
    };
  }

  clearDynamicRecommendations() {
    if (!this.slider) return;
    
    const dynamicSlides = this.slider.querySelectorAll('[data-dynamic-recommendation]');
    dynamicSlides.forEach(slide => slide.remove());
  }

  manageFallbackRecommendations() {
    if (!this.slider) return;
    
    const dynamicSlides = this.slider.querySelectorAll('[data-dynamic-recommendation]:not([style*="display: none"])');
    const fallbackSlides = this.slider.querySelectorAll('[data-fallback-recommendation]:not([style*="display: none"])');
    const visibleRecommendations = dynamicSlides.length + fallbackSlides.length;
    const maxRecommendations = this.settings.recom_max_recommendations;
    
    // Hide excess fallback recommendations
    if (visibleRecommendations > maxRecommendations) {
      const excessCount = visibleRecommendations - maxRecommendations;
      const allFallbackSlides = this.slider.querySelectorAll('[data-fallback-recommendation]');
      const visibleFallbackSlides = Array.from(allFallbackSlides).filter(slide => 
        slide.style.display !== 'none' && !slide.classList.contains('hidden-from-cart')
      );
      
      // Hide from the end
      visibleFallbackSlides.slice(-excessCount).forEach(slide => {
        slide.style.display = 'none';
        slide.classList.add('hidden-from-overflow');
      });
    } else {
      // Show fallback recommendations that were hidden due to overflow
      const overflowHiddenSlides = this.slider.querySelectorAll('[data-fallback-recommendation].hidden-from-overflow');
      Array.from(overflowHiddenSlides).slice(0, maxRecommendations - visibleRecommendations).forEach(slide => {
        if (!slide.classList.contains('hidden-from-cart')) {
          slide.style.display = '';
          slide.classList.remove('hidden-from-overflow');
        }
      });
    }
  }

  updateTitle() {
    if (!this.titleElement) return;
    
    const cartIsEmpty = this.cartProductIds.length === 0;
    const title = cartIsEmpty 
      ? this.settings.recom_title_cart_empty || 'Best Recommendations For You'
      : this.settings.recom_title_cart_nonempty || 'You Might Also Like';
    
    this.titleElement.textContent = title;
  }

  getRandomProductIds(productIds, limit) {
    if (productIds.length <= limit) return [...productIds];
    
    const shuffled = this.shuffleArray([...productIds]);
    return shuffled.slice(0, limit);
  }

  shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  getCartProductIdsFromLocalStorage() {
    try {
      return JSON.parse(localStorage.getItem('cartProductIds') || '[]');
    } catch {
      return [];
    }
  }

  updateCartProductIdsInLocalStorage() {
    localStorage.setItem('cartProductIds', JSON.stringify(this.cartProductIds));
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
} 